// Client for the eRecords REST additions (erecords/*.go in the Go server).
import { useCallback, useEffect, useState } from 'react'
import { pathPrefix } from '../env'

export type CallPause = {
  id: number
  cmID: string
  userID: string
  userName: string
  kind: string // 'voice' or 'sms': which contact method is paused
  phone: string
  until: string
  reason: string
  alertID: number | null
}

export type SyncRun = {
  id: number
  startedAt: string
  finishedAt: string | null
  trigger: string
  triggeredBy: string
  ok: boolean | null
  log: string
}

export type SyncStatus = {
  configured: boolean
  interval: string
  runs: SyncRun[]
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(pathPrefix + '/api/v2/erecords/' + path, {
    credentials: 'same-origin',
    ...init,
  })
  if (!res.ok) {
    throw new Error((await res.text()).trim() || `HTTP ${res.status}`)
  }
  return res.json()
}

export const getCallPauses = (): Promise<CallPause[]> => call('call-pauses')
export const getSyncStatus = (): Promise<SyncStatus> => call('entra-sync')
export const runSync = (): Promise<SyncRun> =>
  call('entra-sync', { method: 'POST' })

// usePolled fetches on mount and every intervalMs; refresh() fetches now.
export function usePolled<T>(
  fn: () => Promise<T>,
  intervalMs: number,
): { data?: T; error?: Error; refresh: () => void } {
  const [data, setData] = useState<T>()
  const [error, setError] = useState<Error>()
  const refresh = useCallback(() => {
    fn()
      .then((d) => {
        setData(d)
        setError(undefined)
      })
      .catch(setError)
  }, [fn])
  useEffect(() => {
    refresh()
    const t = setInterval(refresh, intervalMs)
    return () => clearInterval(t)
  }, [refresh, intervalMs])
  return { data, error, refresh }
}

// ago renders a duration like "3h 12m" between iso and now.
export function ago(iso: string, now = Date.now()): string {
  let s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000))
  const d = Math.floor(s / 86400)
  s -= d * 86400
  const h = Math.floor(s / 3600)
  s -= h * 3600
  const m = Math.floor(s / 60)
  if (d) return `${d}d ${h}h`
  if (h) return `${h}h ${m}m`
  if (m) return `${m}m`
  return 'just now'
}

// hhmmz renders an ISO time as "14:30Z" (UTC, like the pause name suffix).
export function hhmmz(iso: string): string {
  return new Date(iso).toISOString().slice(11, 16) + 'Z'
}

// localTime renders an ISO time in the viewer's zone, e.g. "18:30".
export function localTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export type TeamsUsage = {
  kind: 'policy' | 'schedule'
  id: string
  name: string
  step?: number
}

export type TeamsChannel = {
  value: string
  team: string
  channel: string
  usedBy: TeamsUsage[]
  webUrl?: string
}

export type TeamsWebhook = {
  id: string
  name: string
  workflow: string
  usedBy: TeamsUsage[]
}

export type TeamsStatus = {
  flowConfigured: boolean
  flow: string
  flowUpdatedAt: string | null
  lastTestAt: string | null
  lastTestOK: boolean | null
  lastTestDetail: string
  graphError: string
  channels: TeamsChannel[]
  webhooks: TeamsWebhook[]
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
})

async function send(path: string, init: RequestInit): Promise<void> {
  const res = await fetch(pathPrefix + '/api/v2/erecords/' + path, {
    credentials: 'same-origin',
    ...init,
  })
  if (!res.ok) {
    throw new Error((await res.text()).trim() || `HTTP ${res.status}`)
  }
}

// Which Microsoft Teams teams the Entra sync covers; none selected = the
// admins group only.
export type SyncTeam = { id: string; name: string }
export type SyncUser = { name: string; email: string }
export const syncTeamsApi = {
  get: (): Promise<{ teams: SyncTeam[]; selected: string[] }> =>
    call('entra-sync/teams'),
  preview: (
    ids: string[],
  ): Promise<{ members: number; add: SyncUser[]; remove: SyncUser[] }> =>
    call('entra-sync/teams/preview', json('POST', { ids })),
  set: (ids: string[]): Promise<void> =>
    send('entra-sync/teams', json('PUT', { ids })),
}

export const teamsApi = {
  status: (refresh = false): Promise<TeamsStatus> =>
    call('teams' + (refresh ? '?refresh=1' : '')),
  setFlow: (flowURL: string): Promise<void> =>
    send('teams/flow', json('PUT', { flowURL })),
  test: (channel: string): Promise<{ ok: boolean; detail: string }> =>
    call('teams/test', json('POST', { channel })),
  convert: (webhookID: string, channel: string): Promise<void> =>
    send('teams-convert/' + webhookID, json('POST', { channel })),
}

// Who acknowledged each alert (erecords/acks.go), keyed by alert number.
export type AckInfo = { by: string; userID?: string; at: string; via?: string }

export const getAcks = (ids: number[]): Promise<Record<string, AckInfo>> =>
  ids.length
    ? call('acks?' + ids.map((id) => 'alert=' + id).join('&'))
    : Promise.resolve({})

// useAcks loads "acknowledged by" for the given alerts; it reloads when the
// set of alerts changes (pass only acknowledged ones to keep it small).
export function useAcks(ids: number[]): Record<string, AckInfo> {
  const key = ids.join(',')
  const [acks, setAcks] = useState<Record<string, AckInfo>>({})
  useEffect(() => {
    let live = true
    getAcks(key ? key.split(',').map(Number) : [])
      .then((a) => live && setAcks(a))
      .catch(() => live && setAcks({}))
    return () => {
      live = false
    }
  }, [key])
  return acks
}

// ackLabel: "Dito Kvirikashvili · 2h ago" (via is added when not plain Web).
export function ackLabel(a: AckInfo, now = Date.now()): string {
  const via = (a.via ?? '').replace(/^Web(, )?/, '')
  return `${a.by || 'someone'} · ${ago(a.at, now)} ago${via ? ` · ${via}` : ''}`
}

// pauseEnd says when a pause ends in the viewer's own clock, e.g.
// "back at 20:50 (in 56 min)"; very long pauses read "until resumed".
export function pauseEnd(iso: string, now = Date.now()): string {
  const ms = new Date(iso).getTime() - now
  if (ms > 365 * 24 * 3600 * 1000) return 'until resumed'
  const min = Math.max(1, Math.round(ms / 60000))
  const left =
    min < 60
      ? `${min} min`
      : min < 24 * 60
        ? `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ''}`
        : `${Math.round(min / 1440)} days`
  return `back at ${localTime(iso)} (in ${left})`
}

// The eRecords On-Call bot (erecords/teamsbot*.go): order and timing of Teams
// message, Teams call, SMS and phone call, in minutes.
export type NotifyDelays = {
  teamsChat: number
  teamsCall: number
  sms: number
  voice: number
}

export type BotStatus = {
  configured: boolean
  appId: string
  manifestId: string
  catalogId: string
  messagingEndpoint: string
  delays: NotifyDelays
  people: { name: string; userID: string; chatReady: boolean }[]
}

export const botApi = {
  status: (): Promise<BotStatus> => call('teams-bot'),
  setDelays: (d: NotifyDelays): Promise<NotifyDelays> =>
    call('teams-bot/delays', json('PUT', d)),
  test: (kind: 'chat' | 'call', userID = ''): Promise<void> =>
    send('teams-bot/test', json('POST', { kind, userID })),
  packageURL: pathPrefix + '/api/v2/erecords/teams-bot/app.zip',
}

// useNotifyOrder loads the order people are reached in (for the step dialog).
export function useNotifyOrder():
  | { teams: boolean; delays: NotifyDelays }
  | undefined {
  const [o, setO] = useState<{ teams: boolean; delays: NotifyDelays }>()
  useEffect(() => {
    call<{ teams: boolean; delays: NotifyDelays }>('notify-order')
      .then(setO)
      .catch(() => setO(undefined))
  }, [])
  return o
}

// Per-step "what to send" (erecords/stepchannels.go): channel keys as in
// NotifyDelays; a step without an entry follows the whole order.
export const stepChannelsApi = {
  forPolicy: (policyID: string): Promise<Record<string, string[]>> =>
    call('policies/' + policyID + '/step-channels'),
  set: (stepID: string, channels: string[]): Promise<void> =>
    send('steps/' + stepID + '/channels', json('PUT', { channels })),
}
